// Shared OAuth2 utilities — used by all platform OAuth2 services
// Handles PKCE generation, popup flows, and callback URL parsing
//
// Each platform service (github-oauth2.ts, linear-oauth2.ts, etc.) imports
// from here rather than duplicating this logic.

// ---------------------------------------------------------------------------
// Detection helpers
// ---------------------------------------------------------------------------

/** True when running inside the Electron shell */
export const isElectron =
  typeof window !== 'undefined' &&
  typeof (window as Window & { electronAPI?: unknown }).electronAPI !== 'undefined';

/** Build a CORS-proxy URL for browser environments */
export function proxyUrl(target: string): string {
  const base =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? 'http://localhost:3456'
      : '/__cors-proxy';
  return `${base}?url=${encodeURIComponent(target)}`;
}

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

export function generateRandomString(length: number): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array)
    .map((b) => chars[b % chars.length])
    .join('');
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(plain));
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export async function generateCodeChallenge(
  verifier: string
): Promise<{ challenge: string; method: 'S256' }> {
  return { challenge: base64UrlEncode(await sha256(verifier)), method: 'S256' };
}

// ---------------------------------------------------------------------------
// Redirect URI
// ---------------------------------------------------------------------------

/**
 * Returns the correct redirect URI for the current environment:
 *   Electron → verigit://oauth/callback
 *   Browser  → <origin>/oauth/callback
 */
export function getOAuth2RedirectUri(): string {
  return isElectron
    ? 'verigit://oauth/callback'
    : `${window.location.origin}/oauth/callback`;
}

// ---------------------------------------------------------------------------
// Popup flows
// ---------------------------------------------------------------------------

/**
 * Electron: opens a popup BrowserWindow via IPC and waits for the callback URL.
 * The existing `oauth2:open-window` IPC handler in electron/main.ts is
 * platform-agnostic — it works for any OAuth2 provider.
 */
export function openElectronOAuth2Popup(
  authUrl: string,
  redirectUri: string
): Promise<string> {
  const electronAPI = (
    window as Window & {
      electronAPI?: {
        openOAuth2Window: (url: string, redirectUri: string) => Promise<string>;
      };
    }
  ).electronAPI;

  if (!electronAPI?.openOAuth2Window) {
    throw new Error(
      'Electron IPC not available — ensure preload exposes electronAPI.openOAuth2Window'
    );
  }
  return electronAPI.openOAuth2Window(authUrl, redirectUri);
}

/**
 * Browser: opens a popup window and listens for a postMessage callback.
 * The OAuth2 callback page must post `{ type: callbackMessageType, callbackUrl }`.
 */
export function openBrowserOAuth2Popup(
  authUrl: string,
  callbackMessageType: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    const popup = window.open(authUrl, 'oauth2-popup', 'width=600,height=700,popup=yes');
    if (!popup) {
      reject(new Error('Popup was blocked — allow popups for this site to authenticate'));
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== callbackMessageType) return;
      window.removeEventListener('message', onMessage);
      clearInterval(checkClosed);
      resolve(event.data.callbackUrl as string);
    };

    window.addEventListener('message', onMessage);

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', onMessage);
        reject(new Error('Authentication window was closed'));
      }
    }, 500);
  });
}

// ---------------------------------------------------------------------------
// Callback URL parsing
// ---------------------------------------------------------------------------

/**
 * Parses an OAuth2 callback URL, verifies state, and returns the code.
 * Pass `expectedState: ''` to skip state verification (some flows omit state).
 */
export function parseCallbackUrl(
  callbackUrl: string,
  expectedState: string
): { code: string } {
  const url = new URL(callbackUrl);
  const error = url.searchParams.get('error');
  if (error) {
    const desc = url.searchParams.get('error_description') ?? error;
    throw new Error(`OAuth2 error: ${desc}`);
  }

  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');

  if (!code) throw new Error('No authorisation code in callback URL');
  if (expectedState && returnedState !== expectedState) {
    throw new Error('OAuth2 state mismatch — possible CSRF attack, aborting');
  }

  return { code };
}

// ---------------------------------------------------------------------------
// Token state helper
// ---------------------------------------------------------------------------

export const EXPIRY_BUFFER_MS = 5 * 60 * 1000; // refresh 5 min before expiry
